import {
  useState,
  useEffect,
  useRef,
  type ChangeEvent,
  type FormEvent,
  type FocusEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/react';
import { pushToast } from '../app/toast-store.js';
import { useGlobalErrorShake } from '../../components/ui/error-shake/error-shake-context.js';

interface SettingsInputProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
  error?: string | null;
  required?: boolean;
  autoComplete?: string;
}

function SettingsInput({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  required,
  autoComplete,
}: SettingsInputProps) {
  const borderClass = error ? 'border-b-red-500' : 'border-white/[.04]';
  return (
    <div className="mb-4">
      <label htmlFor={id} className="block text-small text-secondary uppercase tracking-wider mb-1">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        required={required}
        autoComplete={autoComplete}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`w-full bg-transparent border-0 border-b ${borderClass} focus:border-white outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 px-0 py-2 text-primary placeholder:text-secondary`}
      />
      {error ? (
        <p id={`${id}-error`} className="text-small text-secondary mt-1">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function validateRequired(value: string): string | null {
  return value.trim().length === 0 ? 'profile.errors.required' : null;
}

export function ProfileSection() {
  const { t } = useTranslation('settings');
  const shake = useGlobalErrorShake();
  const { isLoaded, user } = useUser();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dirty, setDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const initializedRef = useRef(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (user && !initializedRef.current) {
      setFirstName(user.firstName ?? '');
      setLastName(user.lastName ?? '');
      initializedRef.current = true;
    }
  }, [user]);

  if (!isLoaded) {
    return (
      <section aria-labelledby="settings-profile-heading" className="mb-2">
        <h2 id="settings-profile-heading" className="text-h2 font-semibold text-primary mb-4">
          {t('profile.heading')}
        </h2>
        <div className="h-8 w-64 bg-white/5 animate-pulse rounded mb-4" />
        <div className="h-8 w-64 bg-white/5 animate-pulse rounded mb-4" />
        <div className="h-8 w-64 bg-white/5 animate-pulse rounded" />
      </section>
    );
  }

  if (!user) {
    return null;
  }

  const email = user.primaryEmailAddress?.emailAddress ?? '';

  async function handleSave(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (savingRef.current || !user) return;

    const firstErr = validateRequired(firstName);
    const lastErr = validateRequired(lastName);
    setFirstNameError(firstErr);
    setLastNameError(lastErr);
    if (firstErr || lastErr) return;

    savingRef.current = true;
    setIsSaving(true);
    try {
      await user.update({ firstName, lastName });
      pushToast({ tone: 'success', title: t('profile.saved') });
      setDirty(false);
    } catch {
      pushToast({ tone: 'error', title: t('profile.saveFailed') });
      shake();
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }

  const saveDisabled = !dirty || isSaving || !!firstNameError || !!lastNameError;

  return (
    <section aria-labelledby="settings-profile-heading" className="mb-2">
      <h2 id="settings-profile-heading" className="text-h2 font-semibold text-primary mb-4">
        {t('profile.heading')}
      </h2>
      <form onSubmit={handleSave} noValidate>
        <SettingsInput
          id="settings-first-name"
          label={t('profile.firstNameLabel')}
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            setDirty(true);
            if (firstNameError) setFirstNameError(null);
          }}
          onBlur={(e) => setFirstNameError(validateRequired(e.currentTarget.value))}
          error={firstNameError ? t(firstNameError) : null}
          required
          autoComplete="given-name"
        />
        <SettingsInput
          id="settings-last-name"
          label={t('profile.lastNameLabel')}
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            setDirty(true);
            if (lastNameError) setLastNameError(null);
          }}
          onBlur={(e) => setLastNameError(validateRequired(e.currentTarget.value))}
          error={lastNameError ? t(lastNameError) : null}
          required
          autoComplete="family-name"
        />

        <div className="mb-4">
          <p className="text-small text-secondary uppercase tracking-wider mb-1">
            {t('profile.emailLabel')}
          </p>
          <p className="text-body text-primary">{email}</p>
          <p className="text-small text-secondary mt-1">{t('profile.emailHint')}</p>
        </div>

        <button
          type="submit"
          disabled={saveDisabled}
          data-testid="profile-save"
          className="rounded-lg bg-accent-lime text-[#0a0a0a] px-4 py-2 font-medium hover:brightness-95 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {isSaving ? t('profile.saving') : t('profile.save')}
        </button>
      </form>
    </section>
  );
}
